/// <amd-module name="VSS/Diagnostics/UI/Components/Stats/Stats" />
import * as React from "react";

import { useObservable } from "VSS/Core/Observable";

import { IVssPageContext } from "VSS/Platform/Context";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssPerformanceViewService, IPerformanceView } from "VSS/Diagnostics/PerformanceViewService";
import { PerfSectionDataSegment } from "VSS/Diagnostics/PerfBar";
import { Section } from "VSS/Diagnostics/UI/Section";

import { Observer } from "VSSUI/Observer";

interface StatsData {
    segments: PerfSectionDataSegment[];
    performanceView: IPerformanceView;
    completedScenarioCount: number;
}

function getStatsData(pageContext: IVssPageContext): StatsData {
    const performanceViewService = pageContext.getService<IVssPerformanceViewService>("IVssPerformanceViewService");
    const performanceService = pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    const performanceView = performanceViewService.getPerformanceView();
    const performanceData = performanceService.getPerformanceData();

    // Calculate default Apdex
    let apdex = "frustrated";
    let apdexDecorator = "😢";
    if (performanceData.timeToInteractive < 1000) {
        apdex = "satisfied";
        apdexDecorator = "😀";
    } else if (performanceData.timeToInteractive < 4000) {
        apdex = "tolerating";
        apdexDecorator = "😐";
    }

    const segments = [
        {
            segmentLabel: "TTI",
            segmentTooltip: `Server ${performanceView.getMeasurementDuration("RequestResponse")}ms / Client ${performanceView.getMeasurementDuration(
                "Client-side Rendering"
            )}ms - Apdex: ${apdex}`,
            segmentContent: `${performanceData.timeToInteractive}ms${(performanceData.backgroundTab && " (Background)") || ""} ${apdexDecorator}`
        },
        { segmentLabel: "SQL", segmentContent: performanceData.serverPerformanceData.sql + "" }
    ];

    if (performanceData.serverPerformanceData.rest) {
        segments.push({ segmentLabel: "REST", segmentContent: performanceData.serverPerformanceData.rest + "" });
    }

    if (performanceData.serverPerformanceData.totalRemote !== performanceData.serverPerformanceData.sql) {
        segments.push({ segmentLabel: "Total Remote", segmentContent: performanceData.serverPerformanceData.totalRemote + "" });
    }

    return { segments, performanceView, completedScenarioCount: performanceService.getCompletedScenarios().length };
}

const Stats = (props: IVssComponentContext): JSX.Element => {
    const [showStatsDialog, setShowStatsDialog] = useObservable(false);
    let [statsData, setStatsData] = React.useState(() => getStatsData(props.pageContext));

    const handleScenarioCompleteEvent = () => {
        setStatsData(getStatsData(props.pageContext));
    };

    React.useEffect(() => {
        const performanceService = props.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.subscribe(handleScenarioCompleteEvent, "scenarioComplete");

        if (performanceService.getCompletedScenarios().length > statsData.completedScenarioCount) {
            setStatsData(getStatsData(props.pageContext));
        }

        return () => {
            performanceService.unsubscribe(handleScenarioCompleteEvent, "scenarioComplete");
        };
    }, []);

    return (
        <React.Fragment>
            <Section
                key="performance-section"
                dataSegments={statsData.segments}
                onClick={() => setShowStatsDialog(true)}
                sectionLabel="Performance"
            />
            <Observer showStatsDialog={showStatsDialog}>
                {(props: { showStatsDialog: boolean }) => {
                    return props.showStatsDialog ? (
                        <WrappedComponent
                            dependencies={["ms.vss-features.diag-dialog-content"]}
                            wrappedType={"vss.perf-server-stats-dialog"}
                            onDismiss={() => {
                                setShowStatsDialog(false);
                            }}
                            performanceView={statsData.performanceView}
                        />
                    ) : null;
                }}
            </Observer>
        </React.Fragment>
    );
};

class StatsComponent extends VssComponent {
    public render(): JSX.Element {
        return <Stats {...this.context} />;
    }
}

Components.add("perfbar.statsSection", StatsComponent);
